import { useState } from 'react'
import RecipeCard, { Recipe } from './RecipeCard'
import { RecipeDetailResponse } from '@talktoast/shared'
import { Tabs, RecipeCardSkeleton } from '@talktoast/ui'
import { ToastyLoading } from '@talktoast/shared'
import { ICONS, getIconSize } from '@talktoast/shared'
import {
  MEAL_TYPE_ICONS,
  MealTypeKey
} from '../constants/uiConstants'
import { RecipeModifications } from '../types/recipe'

interface MealPlanDisplayProps {
  recipeDetails: Map<string, RecipeDetailResponse>
  loadingRecipeDetails: Set<string>
  onRerollRecipe: (recipeId: string, recipeName: string, mealType: string, modifications?: RecipeModifications) => void
  onRemoveRecipe?: (recipeName: string) => void
  onAddSlot?: (mealType: string) => void
  addingSlotForMealType?: string | null
  mealTypes?: string[]
  onReplaceWithFavorite?: (recipeId: string, favoriteRecipeName: string) => void
  onNavigateToSubscription?: () => void
  isPolling?: boolean
}

interface AddSlotButtonProps {
  mealType: string
  isAdding: boolean
  onAddSlot?: (mealType: string) => void
}

function AddSlotButton({ mealType, isAdding, onAddSlot }: AddSlotButtonProps) {
  const config = MEAL_TYPE_CONFIG[mealType as MealType]
  const label = config?.label?.toLowerCase() || mealType

  if (isAdding) {
    return <RecipeCardSkeleton badgeCount={3} />
  }

  return (
    <button
      onClick={() => onAddSlot?.(mealType)}
      className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-toast-gold hover:text-toast-gold transition-colors text-sm font-medium flex items-center justify-center gap-2"
    >
      <ICONS.navigation.add className={getIconSize('sm')} />
      Add another {label}
    </button>
  )
}

const MEAL_TYPE_CONFIG = MEAL_TYPE_ICONS

type MealType = MealTypeKey

export default function MealPlanDisplay({
  recipeDetails,
  loadingRecipeDetails,
  onRerollRecipe,
  onRemoveRecipe,
  onAddSlot,
  addingSlotForMealType,
  mealTypes,
  onReplaceWithFavorite,
  onNavigateToSubscription,
  isPolling = false
}: MealPlanDisplayProps) {
  const [activeTab, setActiveTab] = useState<MealType | null>(null)

  // Convert recipeDetails Map to Recipe array
  // Use the real recipe UUID from the database (details.recipe.id) so that
  // downstream calls such as recipeChatService.sendMessage receive a valid UUID
  // rather than a synthetic fallback like "recipe_0" that fails UUID validation.
  const recipes: Recipe[] = Array.from(recipeDetails.entries()).map(([recipeName, details]) => ({
    id: details.recipe.id,
    name: recipeName,
    mealType: details.recipe.mealType || 'dinner'
  }))

  // Group recipes by meal type
  const recipesByMealType = recipes.reduce((acc, recipe) => {
    const mealType = recipe.mealType as MealType
    if (!acc[mealType]) {
      acc[mealType] = []
    }
    acc[mealType].push(recipe)
    return acc
  }, {} as Record<MealType, Recipe[]>)

  // Get meal types that have recipes
  const availableMealTypes = Object.keys(recipesByMealType) as MealType[]

  // Derive the active tab without mutating state during render (setState during render
  // causes a synchronous re-render loop that crashes with React error #130).
  // Also validates that activeTab still has recipes — falls back if it was deleted.
  const effectiveTab = (activeTab && recipesByMealType[activeTab]) ? activeTab : availableMealTypes[0] ?? null

  const totalRecipeCount = recipes.length
  const loadingCount = loadingRecipeDetails.size

  const handleReroll = async (recipeId: string, modifications?: RecipeModifications) => {
    const recipe = recipes.find(r => r.id === recipeId)
    if (recipe) {
      await onRerollRecipe(recipeId, recipe.name, recipe.mealType, modifications)
    }
  }

  // Empty meal plan state (all recipes removed)
  // Guard on !isPolling so the empty state never shows while the first poll tick is in flight
  if (totalRecipeCount === 0 && loadingCount === 0 && !isPolling) {
    const emptyMealTypes = mealTypes && mealTypes.length > 0 ? mealTypes : []
    return (
      <div className="p-4 max-w-4xl mx-auto">
        {emptyMealTypes.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-center text-gray-500 mb-4">Your meal plan is empty. Add a meal to get started.</p>
            {emptyMealTypes.map(mealType => (
              <AddSlotButton
                key={mealType}
                mealType={mealType}
                isAdding={addingSlotForMealType === mealType}
                onAddSlot={onAddSlot}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p className="text-base font-medium text-gray-700 mb-1">Your meal plan is empty.</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Your Meal Plan
          {totalRecipeCount > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-toast-gold/10 text-toast-gold text-xs font-medium rounded-full">
              {totalRecipeCount}
            </span>
          )}
        </h3>
        {loadingCount > 0 && (
          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
            <span className="hidden sm:inline">Fetching {loadingCount} {loadingCount === 1 ? 'recipe' : 'recipes'}...</span>
            <span className="sm:hidden">{loadingCount} left</span>
          </span>
        )}
      </div>

      {/* Desktop: Tabs - Hidden on mobile */}
      {availableMealTypes.length > 0 && (
        <div className="hidden md:block space-y-3">
          <Tabs
            tabs={availableMealTypes.map(mealType => {
              const config = MEAL_TYPE_CONFIG[mealType]
              const count = recipesByMealType[mealType]?.length || 0
              return {
                id: mealType,
                label: (
                  <div className="flex items-center gap-2">
                    <span>{config.emoji}</span>
                    <span>{config.label}</span>
                    <span className="text-xs text-gray-500">({count})</span>
                  </div>
                )
              }
            })}
            activeTab={effectiveTab || availableMealTypes[0]}
            onTabChange={(tabId) => setActiveTab(tabId as MealType)}
          />

          {/* Active Tab Content */}
          {effectiveTab && recipesByMealType[effectiveTab]?.map(recipe => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              recipeDetails={recipeDetails.get(recipe.name) || null}
              isLoadingDetails={loadingRecipeDetails.has(recipe.name)}
              onReroll={handleReroll}
              onDelete={onRemoveRecipe ? () => onRemoveRecipe(recipe.name) : undefined}
              onReplaceWithFavorite={onReplaceWithFavorite}
              onMealTypeFilter={() => {}} // Not needed in this context
              onNavigateToSubscription={onNavigateToSubscription}
            />
          ))}

          {/* Add-slot button for active tab */}
          {effectiveTab && onAddSlot && (
            <AddSlotButton
              mealType={effectiveTab}
              isAdding={addingSlotForMealType === effectiveTab}
              onAddSlot={onAddSlot}
            />
          )}
        </div>
      )}

      {/* Mobile: All meals in grouped list */}
      <div className="md:hidden space-y-4">
        {availableMealTypes.map(mealType => {
          const config = MEAL_TYPE_CONFIG[mealType]
          const meals = recipesByMealType[mealType] || []

          if (meals.length === 0) return null

          return (
            <div key={mealType} className="space-y-3">
              {/* Section header - Only show if multiple meal types */}
              {availableMealTypes.length > 1 && (
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2 px-1">
                  <span className="text-sm">{config.emoji}</span>
                  {config.label}
                </h4>
              )}

              {/* Recipes */}
              {meals.map(recipe => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  recipeDetails={recipeDetails.get(recipe.name) || null}
                  isLoadingDetails={loadingRecipeDetails.has(recipe.name)}
                  onReroll={handleReroll}
                  onDelete={onRemoveRecipe ? () => onRemoveRecipe(recipe.name) : undefined}
                  onReplaceWithFavorite={onReplaceWithFavorite}
                  onMealTypeFilter={() => {}}
                  onNavigateToSubscription={onNavigateToSubscription}
                />
              ))}

              {/* Add-slot button for this meal type section */}
              {onAddSlot && (
                <AddSlotButton
                  mealType={mealType}
                  isAdding={addingSlotForMealType === mealType}
                  onAddSlot={onAddSlot}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* Empty state while loading */}
      {totalRecipeCount === 0 && loadingCount > 0 && (
        <div className="text-center py-8 text-gray-500">
          <ToastyLoading className="mx-auto mb-3 w-16 h-16" />
          <p className="text-sm">Loading your meal plan...</p>
        </div>
      )}
    </div>
  )
}
